import {
  type BreadcrumbData,
  BreadcrumbType,
  type ContentDataItem,
  type PaginationData,
  type SearchResultData,
  type SrpProductData,
  type TechValueBreadcrumb,
} from "../../utils/models";
import { type SearchPageState, SelectionAssistantType } from "./types";
import {
  convertProductToImpression,
  sendCharacteristicsUsage,
  sendSearchSortChoice,
  sendSrpProductClick,
  sendSrpProductListImpression,
  sendValueFilterUsage,
  trackSelectionAssistantRotaryFilters,
} from "../../feature/Analytics/ga/ga";
import { type ThunkAction } from "redux-thunk";
import { type ActionType, type AnyAction } from "../../redux/types";
import { loadCartAmounts } from "../../AddToCart/redux/cartAmounts/actions";
import { type FromUrlSearchParams, generateDataFromUrl, generateUrlFromStore } from "../utils/historyManipulation";
import { getData as fetchSearchResults } from "../api/fetchSearchResults";
import { transformContentSearchResults, transformSearchResults } from "../transformers/SearchResultDataTransformer";
import { type SearchDisplayMode, type SortDirection } from "../models";
import { selectActiveFilterConfiguration, selectHasNonDefaultSort } from "./searchSelectors";
import { getSelectedProductGroupFromSearchResults } from "../utils/searchUtils";
import { getSearchPageType, SearchPageType } from "../useSearchPageType";
import { type RootState } from "../../redux/store";
import { fetchContentSearchResults } from "../api/fetchContentSearchResults";
import { toMapFn } from "../../utils/reducers";
import { redirectToUrl } from "../../utils/url/urlUtils";
import { getAliases } from "../../common/components/Product/ProductAliases";
import { initiallyExpandCategory } from "../utils/seriesTreeUtils";
import { isComingFromSeriesTreeClick } from "./seriesTreeSelectors";
import { type UnitMode } from "../../ImperialMetricData/redux/UnitMode";
import instance from "../../utils/i18n";
import { filterUnavailableParams } from "~/Search/utils/filterUnavailableParams";
import { augmentPDPUrlWithRestriction } from "~/common/components/PackageRestrictionContext/utils/augmentPDPUrlWithRestriction";
import { fetchRecommendedProductsSearch } from "~/Search/api/fetchRecommendedProductsSearch";
import { transformSrpProduct } from "~/Search/transformers/transformSrpProduct";
import { userIsCustomer } from "~/utils/roles";
import { AnalyticsMQ } from "~/feature/Analytics/AnalyticsMQ/AnalyticsMQ";
import { isTechValueFilterCategory } from "~/Search/filterConfigModels";
import { uniqueValues } from "~/utils/misc";

export const UPDATE_SEARCH_PARAMS_FROM_URL = "UPDATE_SEARCH_PARAMS_FROM_URL";
export const SEARCH_START = "SEARCH_START";
export const SEARCH_SUCCESS = "SEARCH_SUCCESS";
export const CONTENT_SEARCH_SUCCESS = "CONTENT_SEARCH_SUCCESS";
export const SEARCH_FAILURE = "SEARCH_FAILURE";

export const CHANGE_SORT = "CHANGE_SORT";
export const CHANGE_SORT_DIRECTION = "CHANGE_SORT_DIRECTION";
export const CHANGE_PAGE = "SRP: Change product results page";
export const CHANGE_CONTENT_PAGE = "SRP: Change content results page";
export const TOGGLE_FACET = "TOGGLE_FACET";
export const REMOVE_BREADCRUMB = "REMOVE_BREADCRUMB";

export const UPDATE_SEARCH_TEXT = "UPDATE_SEARCH_TEXT";
export const CLEAR_ALL = "CLEAR_ALL";
export const APPLY_ALL = "APPLY_ALL";
export const CLEAR_TECH_VALUES = "CLEAR_TECH_VALUES";
export const UPDATE_TECH_VALUE = "UPDATE_TECH_VALUE";

export const SELECT_PRODUCT_GROUP = "SELECT_PRODUCT_GROUP";
export const SELECT_PRODUCT_GROUP_MULTI = "SELECT_PRODUCT_GROUP_MULTI";
export const SET_SELECTED_PRODUCT_GROUPS = "SET_SELECTED_PRODUCT_GROUPS";

export const UPDATE_ASSISTANT_VALUES = "UPDATE_ASSISTANT_VALUES";
export const UPDATE_IS_SELECTION_ASSISTANT = "UPDATE_IS_SELECTION_ASSISTANT";

export const CHANGE_VIEW_MODE = "SRP: Change view mode";

export const UPDATE_CAMPAIGN_FILTER = "UPDATE_CAMPAIGN_FILTER";
export const UPDATE_RELEVANT_FILTER = "UPDATE_RELEVANT_FILTER";

export const SRP_FETCH_RECOMMENDED_PRODUCTS_START = "SRP_FETCH_RECOMMENDED_PRODUCTS_START";
export const SRP_FETCH_RECOMMENDED_PRODUCTS_SUCCESS = "SRP_FETCH_RECOMMENDED_PRODUCTS_SUCCESS";
export const SRP_FETCH_RECOMMENDED_PRODUCTS_FAILURE = "SRP_FETCH_RECOMMENDED_PRODUCTS_FAILURE";

type ThunkResult<R> = ThunkAction<R, RootState, undefined, AnyAction>;
type AsyncThunkResult<R> = ThunkAction<Promise<R>, RootState, undefined, AnyAction>;

export function searchHistoryChanged(pageType: SearchPageType): ThunkResult<void> {
  return (dispatch, getState) => {
    const isLoading = getState().srp.inFlight;
    if (!isLoading) {
      dispatch(searchResultsMounted());
    }
  };
}

export function handleMissingSortAfterTabChangeThunk(): ThunkResult<void> {
  return (dispatch, getState) => {
    const state = getState().srp;
    const currentSort = state.sort.selectedCode;
    if (!state.sort.availableSorts.find((sort) => sort.code === currentSort)) {
      dispatch(changeSort("relevance"));
    }
  };
}

function updateCampaignFilterAction(code?: string | string[]) {
  let payload: string[];
  if (!code) {
    payload = [];
  } else if (Array.isArray(code)) {
    payload = code;
  } else {
    payload = [code];
  }
  return {
    type: UPDATE_CAMPAIGN_FILTER,
    payload,
  } as const;
}

function updateRelevantFilterAction(enabled: boolean) {
  return {
    type: UPDATE_RELEVANT_FILTER,
    payload: enabled,
  } as const;
}

export const updateCampaignFilterThunk = (codes: string[]) => searchAfterwards(updateCampaignFilterAction(codes));
export const updateRelevantFilterThunk = (enabled: boolean) => searchAfterwards(updateRelevantFilterAction(enabled));

export function salesCampaignMounted(code: string): ThunkResult<void> {
  return async (dispatch, getState) => {
    let urlData = generateDataFromUrl();
    urlData = filterUnavailableParams(urlData);
    dispatch(updateStateFromUrl(urlData));
    dispatch(updateCampaignFilterAction(code));
    const isProductGroupSelected = getState().srp.selectedProductGroups.length > 0;

    await dispatch(runSearch(true, !isProductGroupSelected, false));
  };
}

export function searchResultsMounted(): ThunkResult<void> {
  return async (dispatch, getState) => {
    let urlData = generateDataFromUrl();
    urlData = filterUnavailableParams(urlData);
    dispatch(updateStateFromUrl(urlData));
    dispatch(loadCartAmounts());

    AnalyticsMQ.instance.push("searchPageOpen", {});

    const isProductGroupSelected = getState().srp.selectedProductGroups.length > 0;

    await dispatch(runSearch(true, !isProductGroupSelected, false));
  };
}

export function selectionAssistantOnMount(): ThunkResult<void> {
  return async (dispatch, getState) => {
    const state = getState();
    const { imperialMetricData } = state;

    dispatch({
      type: UPDATE_IS_SELECTION_ASSISTANT,
      payload: {
        value: SelectionAssistantType.ROTARY,
      },
    });

    // // Why was this here?
    // await dispatch(runSearch(false, false, url));
    dispatch(updateStateFromUrl(generateDataFromUrl()));

    dispatch(loadCartAmounts());

    await dispatch(runSearch(true, false, false));
  };
}

export function runSearch(
  firstTime: boolean | undefined,
  preselectCategories: boolean | undefined,
  ignoreContent: boolean, // don't update content results
): AsyncThunkResult<SearchResultData | string> {
  return async (dispatch, getStore) => {
    const state = getStore();

    if (!firstTime) {
      window.history.replaceState({}, "", generateUrlFromStore(state));
    }

    dispatch(searchStart());

    try {
      const response = await fetchSearchResults(state.srp, preselectCategories);
      if (response) {
        if (response.keywordRedirectUrl) {
          redirectToUrl(response.keywordRedirectUrl);
          return "redirected";
        }
        const pageType = getSearchPageType();
        const data = transformSearchResults(response, pageType);
        // SCHAEFF-26956 - Sales Campaign page has the redirect feature disabled.
        const srpRedirectEnabled = pageType === SearchPageType.SEARCH && window.SCHA.toggles.srpRedirectEnabled;
        // SCHAEFF-16776 -
        // If "preselectCategories" is true, then this is a search on SRP and the query has changed.
        // In this case, if there's only one search result, we redirect to PDP of that product
        if (
          srpRedirectEnabled &&
          preselectCategories &&
          data.products.length === 1 &&
          data.content.results.length === 0
        ) {
          const product = data.products[0];
          if (product.url) {
            const query = state.srp.text.trim().toLowerCase();

            const isExactMatch =
              query === (product.designation ?? "").trim().toLowerCase() ||
              query.substring(0, product.sapMaterialNumber.length) === product.sapMaterialNumber ||
              getAliases(product, "all").some((alias) => query === alias.trim().toLowerCase());

            const finalUrl = augmentPDPUrlWithRestriction(
              product.url + "?redirect=true" + (isExactMatch ? "" : "&isPartialMatch=true"),
              query,
            );
            redirectToUrl(finalUrl);
            return data;
          }
        }

        dispatch(searchSuccess(data, preselectCategories || false, ignoreContent));
        window.history.replaceState({}, "", generateUrlFromStore(getStore()));

        if (typeof window.SCHA.config.srpPopularProductsPosition === "number") {
          if (state.srp.currentPage === 0) {
            dispatch(fetchSRPCampaignRecommendationsThunk());
          }
        }

        return data;
      } else {
        const msg = `An error occurred`;
        dispatch(searchFailure(msg));
        return msg;
      }
    } catch (e) {
      console.error(e);
      const msg = "A timeout has occured";
      dispatch(searchFailure(msg));
      return msg;
    }
  };
}

export function fetchSRPCampaignRecommendationsThunk(): ThunkResult<void> {
  return async (dispatch, getState) => {
    const state = getState();

    if (!userIsCustomer()) {
      return; // Feature only for customers
    }

    if (!window.SCHA.activeSalesCampaigns.length) {
      return; // There's no campaigns active, so no way there can be recommendations
    }

    dispatch(startFetchingSRPCampaignRecommendations());

    try {
      const data = await fetchRecommendedProductsSearch(state.srp);
      const products = data.map(transformSrpProduct);
      dispatch(fetchSRPCampaignRecommendationsSuccess(products));
    } catch (error) {
      dispatch(fetchSRPCampaignRecommendationsFailure());
    }
  };
}

function startFetchingSRPCampaignRecommendations() {
  return {
    type: SRP_FETCH_RECOMMENDED_PRODUCTS_START,
  } as const;
}

export function fetchSRPCampaignRecommendationsSuccess(products: SrpProductData[]) {
  return {
    type: SRP_FETCH_RECOMMENDED_PRODUCTS_SUCCESS,
    payload: products,
  } as const;
}

function fetchSRPCampaignRecommendationsFailure() {
  return {
    type: SRP_FETCH_RECOMMENDED_PRODUCTS_FAILURE,
  } as const;
}

export function searchAfterwards(action: ActionType<unknown>, ignoreContent: boolean = false): ThunkResult<void> {
  return (dispatch) => {
    dispatch(action);
    // TODO: Make it better
    if (!window.location.href.includes("housingselectionassistant")) {
      dispatch(runSearch(false, false, ignoreContent));
    }
  };
}

function searchStart(): ActionType<void> {
  trackSelectionAssistantRotaryFilters();
  return {
    type: SEARCH_START,
  };
}

export function contentSearchSuccess(data: { results: ContentDataItem[]; pagination: PaginationData }) {
  return {
    type: CONTENT_SEARCH_SUCCESS,
    payload: data,
  } as const;
}

export function searchSuccess(
  data: SearchResultData,
  preselectCategories: boolean,
  ignoreContent?: boolean,
): ThunkResult<void> {
  return (dispatch, getState) => {
    let selectedProductGroups: string[];
    if (preselectCategories) {
      const group = getSelectedProductGroupFromSearchResults(data);
      selectedProductGroups = group ? [group] : [];
    } else {
      selectedProductGroups = getState().srp.selectedProductGroups;
    }
    if (data.seriesTree) {
      initiallyExpandCategory(data.seriesTree, selectedProductGroups);
    }

    const srp = getState().srp;
    AnalyticsMQ.instance.push("searchSuccess", {
      resultAmount: data.pagination.totalNumberOfResults,
      query: srp.text,
      pageNumber: srp.currentPage,
      pageSize: srp.pagination.pageSize,
      webpageType: getSearchPageType(),
    });

    dispatch(searchSuccessAction(data, selectedProductGroups, ignoreContent));
  };
}

export function searchSuccessAction(data: SearchResultData, selectedCategories: string[], ignoreContent?: boolean) {
  return {
    type: SEARCH_SUCCESS,
    payload: {
      data,
      selectedCategories,
      ignoreContent,
    },
  } as const;
}

function searchFailure(error: string) {
  return {
    type: SEARCH_FAILURE,
    payload: error,
  };
}

export function updateStateFromUrl(data: FromUrlSearchParams) {
  return {
    type: UPDATE_SEARCH_PARAMS_FROM_URL,
    payload: data,
  } as const;
}

export const changePage = (pageNumber: number) => searchAfterwards({ type: CHANGE_PAGE, payload: pageNumber }, true);

export const changeContentResultsPage = (pageNumber: number): ThunkResult<void> => {
  return async (dispatch, getState) => {
    dispatch({
      type: CHANGE_CONTENT_PAGE,
      payload: pageNumber,
    });
    const state = getState();
    const query = state.srp.text;

    try {
      const response = await fetchContentSearchResults(query, pageNumber);
      if (response) {
        const data = transformContentSearchResults(response);
        dispatch(contentSearchSuccess(data));
        return data;
      } else {
        const msg = `An error occurred`;
        dispatch(searchFailure(msg));
        return msg;
      }
    } catch (e) {
      const msg = "A timeout has occured";
      dispatch(searchFailure(msg));
      return msg;
    }
  };
};

export const changeSort = (sortCode: string, direction?: SortDirection) => {
  sendSearchSortChoice(sortCode);
  return searchAfterwards(changeSortAction(sortCode, direction), true);
};

export function changeSortAction(sortCode: string, direction?: SortDirection) {
  return {
    type: CHANGE_SORT,
    payload: { sortCode, direction },
  } as const;
}

export const changeSortDirection = (direction: SortDirection) =>
  searchAfterwards(changeSortDirectionAction(direction), true);

export function changeSortDirectionAction(direction: SortDirection) {
  return {
    type: CHANGE_SORT_DIRECTION,
    payload: direction,
  } as const;
}

export interface FacetToggleData {
  newValue: boolean;
  facetValueCode: string;
  facetCode: string;
  facetName: string;
}

export function toggleFacet(facetValueCode: string, facetCategoryCode: string): ThunkResult<void> {
  return (dispatch, getState) => {
    const state = getState();

    const facet = getState().srp.facetValues[facetCategoryCode];
    const facetValue = facet.values.filter((item) => item.code === facetValueCode)[0];
    let fullName;
    if (facet.code === "productSpecific") {
      fullName = `${instance.t(facetValue.name)}`;
    } else {
      fullName = `${facet.name}: ${facetValue.name}`;
    }

    const hasBreadcrumb = state.srp.breadcrumbs.filter(
      (bc) => bc.type === BreadcrumbType.FACET && bc.facetValueCode === facetValueCode,
    )[0];
    const newValue = !hasBreadcrumb;
    dispatch({
      type: TOGGLE_FACET,
      payload: {
        newValue,
        facetValueCode,
        facetCode: facet.code,
        facetName: fullName,
      },
    });
    dispatch(runSearch(false, false, true));

    if (newValue) {
      sendCharacteristicsUsage([facetValueCode]);
    }
  };
}

function removeBreadcrumbInternal(breadcrumb: BreadcrumbData): ActionType<BreadcrumbData> {
  return {
    type: REMOVE_BREADCRUMB,
    payload: breadcrumb,
  };
}

export const removeBreadcrumb = (breadcrumb: BreadcrumbData) =>
  searchAfterwards(removeBreadcrumbInternal(breadcrumb), true);

export function clearAll(searchPageType: SearchPageType): ThunkResult<void> {
  return (dispatch, getState) => {
    const isFromSeriesTree = isComingFromSeriesTreeClick(getState());
    if (!isFromSeriesTree) {
      dispatch(resetProductGroupToLevel1Internal());
    }
    dispatch({
      type: CLEAR_ALL,
      payload: { searchPageType },
    });
    dispatch(runSearch(false, undefined, true));
  };
}

export function clearSearchValueAction(pageType: SearchPageType): ThunkResult<void> {
  return (dispatch) => {
    dispatch(updateSearchText(""));
  };
}

export function updateTechValueAction(code: string, type: string, value?: number) {
  return {
    type: UPDATE_TECH_VALUE,
    payload: {
      code,
      type,
      value,
    },
  };
}

function isTechValueBreadcrumb(bc: BreadcrumbData): bc is TechValueBreadcrumb {
  return bc.type === BreadcrumbType.TECH_VALUE;
}

export function clearAllTechFilters(): ThunkResult<void> {
  return (dispatch, getState) => {
    const state = getState();
    const codesToClear = uniqueValues(state.srp.breadcrumbs.filter(isTechValueBreadcrumb).map((bc) => bc.code));

    dispatch(clearTechFilters(codesToClear));
  };
}

/**
 * Clears all tech values if not supplied a filter
 * @param techValueCategoryTitles
 */
export function clearTechValuesGroup(techValueFilterTitles: string | string[]): ThunkResult<void> {
  return (dispatch, getState) => {
    const state = getState();
    const config = selectActiveFilterConfiguration(getState());

    if (typeof techValueFilterTitles === "string") {
      techValueFilterTitles = [techValueFilterTitles];
    }
    const validTitles = toMapFn(
      techValueFilterTitles,
      (title) => title,
      () => true,
    );

    const techValueFiltersToClear = config
      .filter(isTechValueFilterCategory)
      .flatMap((category) => category.items)
      .filter((category) => validTitles[category.title]);

    if (techValueFiltersToClear.length > 0) {
      const codesToClear = techValueFiltersToClear.map((techValueFilter) => {
        return techValueFilter.uid;
      });
      dispatch(clearTechFilters(codesToClear));
    }
  };
}

export function clearTechFilters(techFilterUids: string | string[]): ThunkResult<void> {
  return (dispatch, getState) => {
    const codesToClear = Array.isArray(techFilterUids) ? techFilterUids : [techFilterUids];
    if (!codesToClear.length) {
      return;
    }
    const breadCrumbsToClear = getState().srp.breadcrumbs.filter(
      (bc) => bc.type === BreadcrumbType.TECH_VALUE && codesToClear.indexOf(bc.code) !== -1,
    );
    dispatch({ type: CLEAR_TECH_VALUES, payload: codesToClear });
    breadCrumbsToClear.forEach((bc) => {
      dispatch(removeBreadcrumbInternal(bc));
    });
    dispatch(runSearch(false, false, true));
  };
}

export function applyTechValueGroup(): ThunkResult<void> {
  return (dispatch, getState) => {
    const state = getState();
    const techFilterCategories = selectActiveFilterConfiguration(state)?.filter(isTechValueFilterCategory) ?? [];

    const srp = state.srp;
    const raw = srp.rawTechFilterValues;
    const applied = srp.appliedTechFilterValues;
    techFilterCategories.forEach((category, index) => {
      const activeFilterCodes = category.items
        .filter((filter) => {
          return (
            (raw[`${filter.uid}.min`] !== applied[`${filter.uid}.min`] && raw[`${filter.uid}.min`]) ||
            (raw[`${filter.uid}.max`] !== applied[`${filter.uid}.max`] && raw[`${filter.uid}.max`])
          );
        })
        .map((filter) => filter.uid);
      if (activeFilterCodes.length > 0) {
        sendValueFilterUsage(activeFilterCodes, index);
      }
    });

    dispatch(applyAllFiltersAction(state.imperialMetricData.unitMode));

    dispatch(runSearch(false, false, true));
  };
}

export function applyAllFiltersAction(unitMode: UnitMode) {
  return {
    type: APPLY_ALL,
    payload: {
      unitMode,
    },
  } as const;
}

export function updateSearchText(searchText: string): ThunkResult<void> {
  return async (dispatch) => {
    dispatch({
      type: UPDATE_SEARCH_TEXT,
      payload: { searchText, searchPageType: getSearchPageType() },
    });

    await dispatch(runSearch(false, true, false));
  };
}

function getActiveCharacteristicGroups(state: SearchPageState) {
  const facetCodes = Object.keys(state.facetValues);
  const activeFacetCodes = facetCodes.filter((code) =>
    state.breadcrumbs.some((breadcrumb) => {
      if (breadcrumb.type === BreadcrumbType.FACET) {
        return breadcrumb.facetCode === code;
      }
      return false;
    }),
  );
  return activeFacetCodes;
}

function areValueFiltersActive(state: SearchPageState) {
  return state.breadcrumbs.some((bc) => bc.type === BreadcrumbType.TECH_VALUE);
}

export function searchItemClick(area: string, product: SrpProductData): ThunkResult<void> {
  return (dispatch, getState) => {
    const state = getState();
    const srp = state.srp;
    const hasValueFilterActive = areValueFiltersActive(srp);
    const hasCharacteristics = getActiveCharacteristicGroups(srp).length > 0;
    const hasFilteringActive = hasValueFilterActive || hasCharacteristics;

    const hasNonDefaultSort = selectHasNonDefaultSort(state);

    const position =
      1 + srp.results.findIndex((row) => row.productCode === product.code) + srp.pagination.pageSize * srp.currentPage;

    sendSrpProductClick(product, hasFilteringActive, hasNonDefaultSort, area, position);
  };
}

export function searchAfterScroll(oldLastSeen: number, newLastSeen: number): ThunkResult<void> {
  return (dispatch, getState) => {
    // We didn't see anything new - no impressions to send
    if (oldLastSeen >= newLastSeen) {
      return;
    }

    const state = getState();
    const srp = state.srp;
    const basicProducts = state.products.basic;
    const productPrices = state.products.price;
    const productAvailabilities = state.products.availability;
    const hasValueFilterActive = areValueFiltersActive(srp);
    const hasCharacteristics = getActiveCharacteristicGroups(srp).length > 0;
    const hasFilteringActive = hasValueFilterActive || hasCharacteristics;

    const hasNonDefaultSort = selectHasNonDefaultSort(state);

    const searchResultRows = srp.results.slice(oldLastSeen, newLastSeen);

    const pageType = getSearchPageType();

    const impressions = searchResultRows.map((row, index) => {
      // TODO: Use a Product Descriptor here?
      const code = row.productCode;
      const product = basicProducts[code];
      const prices = productPrices[code]?.data;
      const price = prices?.length === 1 ? prices[0] : undefined;
      const availability = productAvailabilities[code]?.data ?? undefined;
      const impression = convertProductToImpression({ basic: product, price, availability });
      // TODO - Other cases? Campaign!
      impression.list = pageType === SearchPageType.PLP ? window.SCHA.pageCategory : "Search Results";
      impression.position = 1 + oldLastSeen + index + srp.pagination.pageSize * srp.currentPage;
      return impression;
    });

    sendSrpProductListImpression(impressions, hasFilteringActive, hasNonDefaultSort);
  };
}

export function selectProductGroupAction(code: string) {
  return {
    type: SELECT_PRODUCT_GROUP,
    payload: {
      code,
    },
  } as const;
}

export function selectProductGroup(code: string = ""): ThunkResult<void> {
  return async (dispatch, getState) => {
    dispatch(selectProductGroupAction(code));
    dispatch(runSearch(false, undefined, true));
  };
}

export function selectProductGroupMultipleAction(code: string) {
  return {
    type: SELECT_PRODUCT_GROUP_MULTI,
    payload: {
      code,
    },
  } as const;
}

export function setSelectedProductGroupsThunk(values: string | string[]): ThunkResult<void> {
  return async (dispatch, getState) => {
    dispatch(setSelectedProductGroups(values));
    dispatch(runSearch(false, undefined, true));
  };
}

export function setSelectedProductGroups(values: string | string[]) {
  return {
    type: SET_SELECTED_PRODUCT_GROUPS,
    payload: {
      values: Array.isArray(values) ? values : [values],
    },
  } as const;
}

export function selectProductGroupWithUrl(code: string): ThunkResult<void> {
  return async (dispatch, getState) => {
    dispatch(selectProductGroupMultipleAction(code));
    dispatch(runSearch(false, false, true));
  };
}

function resetProductGroupToLevel1Internal(): ThunkResult<void> {
  return (dispatch, getState) => {
    const state = getState();
    const selectedProductGroup = state.srp.selectedProductGroups[0] ?? "";
    const newCode = selectedProductGroup
      .split("/")
      .filter((_, i) => i < 3)
      .join("/");

    dispatch(selectProductGroupAction(newCode));
  };
}

export const updateAssistantValues = (code: string): ThunkResult<void> => {
  return async (dispatch) => {
    await dispatch({
      type: UPDATE_ASSISTANT_VALUES,
      payload: {
        code,
      },
    });

    await dispatch(runSearch(false, false, true));
  };
};

export const changeSearchDisplayModeAction = (mode: SearchDisplayMode) => ({
  type: CHANGE_VIEW_MODE,
  payload: mode,
});
